import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Th, Td } from "@/components/common";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Undo2 } from "lucide-react";

const NONE = "__none__";

/** Zusatzfelder je Wareneingangsposition: Charge, MHD, Lagerplatz, Qualität.
 *  Für einfache Non-Food-Eingänge bleiben sie eingeklappt. */
export const ReceiptLineExtras = ({ line, index, variant, locations, locationsEnabled, warehouseId, onChange }) => {
  const { t } = useI18n();
  const tracked = !!variant?.expiry_tracked;
  const [open, setOpen] = useState(tracked);
  useEffect(() => { if (tracked) setOpen(true); }, [tracked]);

  return (
    <div className="w-full">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`receipt-extras-toggle-${index}`}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {t("wg.details")}
        {tracked && <span className="ml-1 rounded-full bg-chart-3/20 px-2 py-0.5 text-chart-3"
          data-testid={`receipt-bbd-required-${index}`}>{t("wg.bbdRequired")}</span>}
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-1 gap-2 rounded-md border border-border p-2 sm:grid-cols-2">
          <div>
            <Label className="text-xs">{t("wg.supplierLot")}</Label>
            <Input className="mt-1 h-9" value={line.lot_number || ""} data-testid={`receipt-lot-${index}`}
              onChange={(e) => onChange("lot_number", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">{t("wg.bbd")}</Label>
            <Input type="date" className="mt-1 h-9" value={line.best_before_date || ""}
              data-testid={`receipt-bbd-${index}`}
              onChange={(e) => onChange("best_before_date", e.target.value)} />
            {!tracked && <p className="mt-1 text-xs text-muted-foreground">{t("wg.bbdNotRequired")}</p>}
          </div>
          <div>
            <Label className="text-xs">{t("wg.quality")}</Label>
            <Select value={line.quality || "good"} onValueChange={(v) => onChange("quality", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid={`receipt-quality-${index}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="good">{t("common.qualityGood", "A-Ware")}</SelectItem>
                <SelectItem value="second">{t("common.qualitySecond", "B-Ware")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {locationsEnabled && (
            <div>
              <Label className="text-xs">{t("wg.location")}</Label>
              <Select value={line.storage_location_id || NONE}
                onValueChange={(v) => onChange("storage_location_id", v === NONE ? "" : v)}>
                <SelectTrigger className="mt-1 h-9" data-testid={`receipt-location-${index}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {locations.filter((l) => l.active !== false && (!warehouseId || l.warehouse_id === warehouseId)).map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.code}{l.name && l.name !== l.code ? ` · ${l.name}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Storno-Dialog: Vorschau je Position, Menge maximal bis zur serverseitig ermittelten Grenze. */
export const ReversalDialog = ({ receipt, onClose, onDone }) => {
  const { t, fmtDate } = useI18n();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!receipt) return;
    setData(null); setQty({}); setReason("");
    api.get(`/purchase-receipts/${receipt.id}/reversal-preview`)
      .then((r) => {
        setData(r.data);
        setQty(Object.fromEntries(r.data.rows.map((x) => [x.movement_id, x.reversible])));
      })
      .catch((e) => { toast.error(apiError(e)); closeRef.current(); });
  }, [receipt]);

  const submit = async () => {
    setBusy(true);
    try {
      // Bewusst ALLE Zeilen senden (auch 0): so bleibt es beim Backend ein Teilstorno und
      // niemals versehentlich ein Vollstorno.
      const lines = (data?.rows || [])
        .map((r) => ({ movement_id: r.movement_id, quantity: Number(qty[r.movement_id]) || 0 }));
      const res = await api.post(`/purchase-receipts/${receipt.id}/reverse`, { reason, lines });
      toast.success(`${t("wg.reverseDone")} · ${res.data.quantity}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const total = Object.values(qty).reduce((a, v) => a + (Number(v) || 0), 0);

  return (
    <Dialog open={!!receipt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{t("wg.reverse")}</DialogTitle></DialogHeader>
        {!data ? <div className="text-muted-foreground">{t("common.loading")}</div> : (
          <div className="space-y-3">
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-muted/50"><tr>
                  <Th>{t("common.article")}</Th><Th>{t("wg.lot")}</Th><Th>{t("wg.bbd")}</Th>
                  <Th right>{t("wg.booked")}</Th><Th right>{t("wg.consumed")}</Th>
                  <Th right>{t("wg.reversible")}</Th><Th right>{t("wg.reverseQty")}</Th>
                </tr></thead>
                <tbody data-testid="reversal-rows">
                  {data.rows.map((r) => (
                    <tr key={r.movement_id}>
                      <Td>{r.sku || "—"}</Td>
                      <Td>{r.lot_number || "—"}</Td>
                      <Td>{r.best_before_date ? fmtDate(r.best_before_date) : "—"}</Td>
                      <Td right mono>{r.booked}</Td>
                      <Td right mono>{r.consumed}</Td>
                      <Td right mono data-testid={`reversible-${r.movement_id}`}>{r.reversible}</Td>
                      <Td right>
                        <Input type="number" min="0" max={r.reversible} className="h-8 w-20 text-right"
                          data-testid={`reverse-qty-${r.movement_id}`}
                          value={qty[r.movement_id] ?? 0} disabled={r.reversible === 0}
                          onChange={(e) => setQty({ ...qty, [r.movement_id]: Math.max(0, Math.min(r.reversible, Number(e.target.value) || 0)) })} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.reversible_total === 0 && (
              <p className="text-sm text-chart-4" data-testid="reversal-none">{t("wg.nothingReversible")}</p>
            )}
            <div>
              <Label>{t("wg.reverseReason")}</Label>
              <Input className="mt-1.5" value={reason} data-testid="reversal-reason"
                onChange={(e) => setReason(e.target.value)} />
            </div>
            <p className="text-xs text-muted-foreground">{t("wg.reverseHint")}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="destructive" onClick={submit} data-testid="reversal-submit"
            disabled={busy || !data || total <= 0}>
            <Undo2 className="mr-1.5 h-4 w-4" /> {t("wg.reverse")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
